import React, { useMemo } from 'react'
import styled from 'styled-components'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import useDefaultAirport from 'hooks/useDefaultAirport'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPrice'
import { unique } from 'lib/array/arrayUtils'

const Pricing = styled.div`
  display: flex;
  flex-direction: column;
`

interface Props {
  offer: App.Offer | App.OfferSummary
  pkg: App.Package
}

function HeroOfferTileBundlePricing(props: Props) {
  const { offer, pkg } = props
  const defaultAirport = useDefaultAirport()

  const flightPrice = defaultAirport?.code ? offer.flightPrices[defaultAirport.code] : undefined

  const {
    totalPrice,
    totalValue,
    saleUnit,
    valueOffPercent,
  } = useOfferPackagePrice(pkg, offer, { flightPrice })

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  if (!flightPrice) {
    return (
      <Pricing>
        <CaptionTextBlock colour="neutral-one" variant="medium" weight="bold" startIcon={<LinePlaneFlyIcon colour="neutral-one" />}>
          Hotel + Flights packages
        </CaptionTextBlock>
      </Pricing>
    )
  }

  const showValue = pkg.shouldDisplayValue && totalValue > totalPrice
  const shouldShowRate = (valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE) && !offer.isDiscountPillHidden

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={4}>
      <Group direction="vertical">
        <CaptionTextBlock colour="neutral-one" variant="medium" weight="bold" startIcon={<LinePlaneFlyIcon colour="neutral-one" />}>
          Hotel + Flights
        </CaptionTextBlock>
        <PriceRowPriceCaption>{offer.tileDurationLabel} from</PriceRowPriceCaption>
        <PriceRowPrice
          size="L"
          price={Math.ceil(totalPrice)}
          saleUnit={saleUnit}
        />
        <PriceRowTaxesAndFees />
        {showValue && <PriceRowValueDiscountWithCaption
          size="L"
          originalValue={Math.ceil(totalValue)}
          discountPercentage={shouldShowRate ? valueOffPercent : undefined}
        />}
        <PriceRowCaption>
          2 adults
        </PriceRowCaption>
      </Group>
      <PriceRowAgentHubCommission
        size="L"
        productType={offer.productType}
        offerId={offer.id}
        offerLocations={offerLocations}
        vendorName={vendorName}
        packageId={pkg.id}
      />
      <CSSBreakpoint min="desktop">
        <TextButton kind="primary">
          View offer
        </TextButton>
      </CSSBreakpoint>
    </Group>
  )
}

export default HeroOfferTileBundlePricing
